import { auth } from './firebase';
import { WorkspaceScope, PublicInactiveNotice } from '../types/workspace';
import type { CharacterTheme } from '../config/characterThemeAssets.mjs';

export class WorkspaceApiError extends Error {
  constructor(message: string, public notice?: PublicInactiveNotice, public code = 'request-rejected', public status = 0, public characterTheme?: CharacterTheme) { super(message); }
}

export async function workspaceApi<T>(action: string, scope: WorkspaceScope | null, data: unknown = {}): Promise<T> {
  const token = action !== 'logout' && scope?.mode !== 'public' ? await auth.currentUser?.getIdToken() : undefined;
  let response: Response;
  try { response = await fetch('/.netlify/functions/workspace-api', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    credentials: 'same-origin',
    body: JSON.stringify({ action, workspaceId: scope?.workspaceId, ...Object(data) })
  }); } catch {
    throw new WorkspaceApiError('서버에 연결할 수 없습니다. 네트워크와 서버 실행 상태를 확인해주세요.', undefined, 'api-unreachable');
  }
  if (!response.headers.get('content-type')?.includes('application/json')) {
    throw new WorkspaceApiError('로그인 API를 사용할 수 없습니다. 서버 실행 상태를 확인해주세요.', undefined, 'api-route-unavailable', response.status);
  }
  let result: { data?: T; error?: string; code?: string; notice?: PublicInactiveNotice; characterTheme?: CharacterTheme };
  try { result = await response.json(); } catch {
    throw new WorkspaceApiError('서버 응답을 확인할 수 없습니다.', undefined, 'api-invalid-response', response.status);
  }
  if (!result || typeof result !== 'object' || (!('data' in result) && !result.error)) {
    throw new WorkspaceApiError('서버 응답을 확인할 수 없습니다.', undefined, 'api-invalid-response', response.status);
  }
  if (!response.ok || result.error) throw new WorkspaceApiError(result.error || '요청을 처리할 수 없습니다.', result.notice, result.code || 'request-rejected', response.status, result.characterTheme);
  return result.data as T;
}
